import { Injectable } from '@angular/core';

/**
 * Cache Configuration Interface
 */
export interface CacheConfig {
  urlPattern: string;    // URL pattern to match (can be partial)
  ttl: number;          // Time-to-live in milliseconds
  maxSize: number;      // Maximum number of entries for this pattern
  description: string;  // Human-readable description
  priority: number;     // Cache priority (higher = more important)
}

/**
 * Cache Configuration Service - Phase 3A Implementation
 *
 * Manages caching policies for different API endpoints.
 * Provides centralized configuration for cache TTL, patterns, and behaviors.
 *
 * Design principles:
 * - Conservative TTL values to avoid stale data
 * - Selective caching for safe endpoints only
 * - Easy configuration management
 * - Performance monitoring support
 */
@Injectable({
  providedIn: 'root'
})
export class CacheConfigService {

  private configs: CacheConfig[] = [
    // User-related data (medium TTL)
    {
      urlPattern: 'user/',
      ttl: 5 * 60 * 1000,        // 5 minutes
      maxSize: 100,
      description: 'User profile and basic information',
      priority: 8
    },

    // Business role and permission data (medium TTL)
    {
      urlPattern: 'business/findUserRoleInBusiness',
      ttl: 5 * 60 * 1000,        // 5 minutes
      maxSize: 50,
      description: 'Business role and permission data - high impact',
      priority: 9
    },

    // Business settings and configuration (longer TTL)
    {
      urlPattern: 'business/settings',
      ttl: 10 * 60 * 1000,       // 10 minutes
      maxSize: 50,
      description: 'Business configuration and settings',
      priority: 7
    },

    // Static reference data (long TTL)
    {
      urlPattern: 'static/',
      ttl: 30 * 60 * 1000,       // 30 minutes
      maxSize: 200,
      description: 'Static reference data (countries, timezones, etc.)',
      priority: 6
    },

    // Subscription and plan data (medium TTL)
    {
      urlPattern: 'subscription',
      ttl: 10 * 60 * 1000,       // 10 minutes
      maxSize: 30,
      description: 'Subscription plans and pricing data',
      priority: 5
    },

    // Company/organization data (medium TTL)
    {
      urlPattern: 'organization/',
      ttl: 8 * 60 * 1000,        // 8 minutes
      maxSize: 40,
      description: 'Organization and company information',
      priority: 6
    },

    // Template and configuration data (long TTL)
    {
      urlPattern: 'template/',
      ttl: 15 * 60 * 1000,       // 15 minutes
      maxSize: 100,
      description: 'Email templates and configuration data',
      priority: 4
    }
  ];

  // Explicitly excluded endpoints (never cache these)
  private excludedPatterns: string[] = [
    'auth/',              // Authentication endpoints
    'login',              // Login/logout
    'logout',
    'token',              // Token refresh
    'message/',           // Real-time messages
    'notification/',      // Live notifications
    'status/',            // Status updates
    'metrics/',           // Live metrics
    'analytics/',         // Real-time analytics
    'search/',            // Search results (user-specific)
    'upload/',            // File uploads
    'download/',          // File downloads
    'stream/',            // Real-time streams
    'socket/',            // WebSocket endpoints
    'live/',              // Any live data
    'parseResume',        // Resume parsing - TESTING MODE (comment out after testing)
    'user/profile/complete/',  // Real-time usage data (connectionCnt, postCnt, candidatesCount)
    'user/getBadgeDetails/',   // Real-time plan usage data (includedContactsCount, interactionCount)
    // Every avatar list reads this one — post authors, business employees, business
    // followers. The `user/` config below matched it, so a member who uploaded a new
    // profile picture kept showing the old one to everyone else until the entry aged
    // out or they hard-refreshed the tab. Photos have to be current, so never cache it.
    'user/connection/'
  ];

  // Never cache the single-user profile GET `user/{uuid}` — the profile page must
  // always show the latest data. The id is a dynamic UUID, so it can't be listed
  // as a literal substring in excludedPatterns; this shape-match targets exactly
  // `user/{uuid}` (UUID as the final path segment) and leaves other `user/...`
  // GETs cached as before.
  private readonly USER_BY_ID_PATTERN =
    /(?:^|\/)user\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(?:$|[?#])/;

  /**
   * Get cache configuration for a given URL
   * @param url - The full URL to check
   * @returns CacheConfig if URL should be cached, null otherwise
   */
  getConfigForUrl(url: string): CacheConfig | null {
    // Check if URL is explicitly excluded
    if (this.isExcludedUrl(url)) {
      return null;
    }

    // Find matching configuration (prioritize most specific matches)
    const matchingConfigs = this.configs.filter(config =>
      url.includes(config.urlPattern)
    );

    if (matchingConfigs.length === 0) {
      return null;
    }

    // Return the most specific match (longest pattern match)
    return matchingConfigs.reduce((best, current) =>
      current.urlPattern.length > best.urlPattern.length ? current : best
    );
  }

  /**
   * Check if URL should never be cached
   */
  private isExcludedUrl(url: string): boolean {
    // Single-user profile GET `user/{uuid}` is never cached (always fetch fresh).
    if (this.USER_BY_ID_PATTERN.test(url)) {
      return true;
    }
    return this.excludedPatterns.some(pattern =>
      url.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Get all cache configurations
   */
  getAllConfigs(): CacheConfig[] {
    return [...this.configs].sort((a, b) => b.priority - a.priority);
  }

  /**
   * Get excluded URL patterns
   */
  getExcludedPatterns(): string[] {
    return [...this.excludedPatterns];
  }

  /**
   * Add a new cache configuration
   * @param config - Cache configuration to add
   */
  addConfig(config: CacheConfig): void {
    // Validate configuration
    if (!config.urlPattern || config.ttl <= 0 || config.maxSize <= 0) {
      throw new Error('Invalid cache configuration');
    }

    // Check for duplicates
    const exists = this.configs.some(c => c.urlPattern === config.urlPattern);
    if (exists) {
      throw new Error(`Configuration for pattern '${config.urlPattern}' already exists`);
    }

    this.configs.push(config);
    console.log(`✅ Added cache config for pattern: ${config.urlPattern}`);
  }

  /**
   * Remove cache configuration
   * @param urlPattern - URL pattern to remove
   */
  removeConfig(urlPattern: string): boolean {
    const initialLength = this.configs.length;
    this.configs = this.configs.filter(config => config.urlPattern !== urlPattern);

    if (this.configs.length < initialLength) {
      console.log(`🗑️ Removed cache config for pattern: ${urlPattern}`);
      return true;
    }
    return false;
  }

  /**
   * Update existing cache configuration
   * @param urlPattern - Pattern to update
   * @param updates - Partial updates to apply
   */
  updateConfig(urlPattern: string, updates: Partial<CacheConfig>): boolean {
    const config = this.configs.find(c => c.urlPattern === urlPattern);
    if (!config) {
      return false;
    }

    Object.assign(config, updates);
    console.log(`🔄 Updated cache config for pattern: ${urlPattern}`);
    return true;
  }

  /**
   * Add URL pattern to exclusion list
   * @param pattern - Pattern to exclude from caching
   */
  addExcludedPattern(pattern: string): void {
    if (!this.excludedPatterns.includes(pattern)) {
      this.excludedPatterns.push(pattern);
      console.log(`🚫 Added excluded pattern: ${pattern}`);
    }
  }

  /**
   * Remove URL pattern from exclusion list
   * @param pattern - Pattern to remove from exclusions
   */
  removeExcludedPattern(pattern: string): boolean {
    const index = this.excludedPatterns.indexOf(pattern);
    if (index > -1) {
      this.excludedPatterns.splice(index, 1);
      console.log(`✅ Removed excluded pattern: ${pattern}`);
      return true;
    }
    return false;
  }

  /**
   * Get configuration summary for monitoring
   */
  getSummary(): any {
    return {
      totalConfigs: this.configs.length,
      excludedPatterns: this.excludedPatterns.length,
      averageTTL: this.configs.reduce((sum, c) => sum + c.ttl, 0) / this.configs.length,
      configsByPriority: this.configs.reduce((acc, config) => {
        acc[config.priority] = (acc[config.priority] || 0) + 1;
        return acc;
      }, {} as Record<number, number>),
      patterns: this.configs.map(c => ({
        pattern: c.urlPattern,
        ttl: c.ttl,
        priority: c.priority
      }))
    };
  }

  /**
   * Validate cache configuration
   * @param config - Configuration to validate
   */
  validateConfig(config: CacheConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.urlPattern || config.urlPattern.trim().length === 0) {
      errors.push('URL pattern is required');
    }

    if (config.ttl <= 0) {
      errors.push('TTL must be greater than 0');
    }

    if (config.ttl > 60 * 60 * 1000) { // 1 hour
      errors.push('TTL should not exceed 1 hour for safety');
    }

    if (config.maxSize <= 0) {
      errors.push('Max size must be greater than 0');
    }

    if (config.maxSize > 1000) {
      errors.push('Max size should not exceed 1000 for memory efficiency');
    }

    if (config.priority < 1 || config.priority > 10) {
      errors.push('Priority must be between 1 and 10');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get recommended TTL for endpoint type
   * @param endpointType - Type of endpoint (static, user, business, etc.)
   */
  getRecommendedTTL(endpointType: string): number {
    const recommendations = {
      'static': 30 * 60 * 1000,      // 30 minutes
      'user': 5 * 60 * 1000,         // 5 minutes
      'business': 5 * 60 * 1000,     // 5 minutes
      'settings': 10 * 60 * 1000,    // 10 minutes
      'reference': 15 * 60 * 1000,   // 15 minutes
      'template': 15 * 60 * 1000,    // 15 minutes
      'subscription': 10 * 60 * 1000  // 10 minutes
    };

    return recommendations[endpointType] || 5 * 60 * 1000; // Default: 5 minutes
  }
}